import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faTimesCircle, faCalendarAlt, faEye, faEyeSlash, faCubes, faRulerCombined, faBoxOpen, faInfoCircle, faGlobe, faFileInvoice, faTag } from '@fortawesome/free-solid-svg-icons';
import { keyUserRoles } from '../../../assets/js/SessionStorageNames';

// Función para verificar si un producto está revisado
const isProductReviewed = (productId) => {
  try {
    const reviewedProducts = JSON.parse(localStorage.getItem('reviewedProducts') || '[]');
    return reviewedProducts.includes(productId);
  } catch (error) {
    console.error('Error al verificar si está revisado:', error);
    return false;
  }
};

const STATUS_LABELS = {
  pending: 'Pendiente',
  processing: 'En proceso',
  approved: 'Aprobado',
  rejected: 'Rechazado',
  failed: 'Fallido',
  cancelled: 'Cancelado',
};

const STATUS_COLORS = {
  pending: { bg: '#fff8e1', color: '#f57f17', border: '#ffe082' },
  processing: { bg: '#e3f2fd', color: '#1565c0', border: '#90caf9' },
  approved: { bg: '#e8f5e9', color: '#2e7d32', border: '#a5d6a7' },
  rejected: { bg: '#ffebee', color: '#c62828', border: '#ef9a9a' },
  failed: { bg: '#fce4ec', color: '#c62828', border: '#f48fb1' },
  cancelled: { bg: '#fbe9e7', color: '#bf360c', border: '#ffab91' },
};

const INVOICE_POLICY_LABELS = {
  order: 'Cantidades ordenadas',
  delivery: 'Cantidades entregadas',
};

// Estilos CSS para animaciones
const styles = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
  @keyframes modalFadeIn {
    from { opacity: 0; transform: scale(0.95); }
    to { opacity: 1; transform: scale(1); }
  }
`;

// Componente de sección reutilizable
const Section = ({ icon, title, children, style }) => (
  <div style={{ marginBottom: 20, ...style }}>
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      marginBottom: 12,
      paddingBottom: 8,
      borderBottom: '2px solid #e8ecf1',
    }}>
      {icon && <FontAwesomeIcon icon={icon} style={{ color: '#5b6abf', fontSize: 15 }} />}
      <span style={{ fontWeight: 700, fontSize: 15, color: '#2c3e50', letterSpacing: '0.3px' }}>{title}</span>
    </div>
    {children}
  </div>
);

// Componente de campo de detalle
const DetailField = ({ label, value, fullWidth = false }) => {
  if (value === undefined || value === null || value === '') return null;
  return (
    <div style={{
      flex: fullWidth ? '1 1 100%' : '1 1 calc(50% - 8px)',
      minWidth: fullWidth ? '100%' : 200,
      marginBottom: 8,
    }}>
      <div style={{ fontSize: 12, color: '#8899a6', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 2 }}>
        {label}
      </div>
      <div style={{ fontSize: 14, color: '#2c3e50', fontWeight: 500 }}>
        {typeof value === 'boolean' ? (value ? 'Sí' : 'No') : value}
      </div>
    </div>
  );
};

const ProductModalWidget = ({
  product,
  open,
  onClose,
  showApproveReject = false,
  onApprove,
  onReject,
  rejectDisabled = false,
  approveDisabled = false,
  rejectReason,
  setRejectReason,
  convertBase64ToDataUrl,
  onShowAdditionalImages, 
  onCancelOrder,
  onMarkAsReviewed,
  showReviewButton = false,
}) => {
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [isReviewed, setIsReviewed] = useState(false);
  const [reviewLoading, setReviewLoading] = useState(false);

  // Verificar si el usuario es admin
  const userRoles = localStorage.getItem(keyUserRoles)?.split(",").filter(String) || [];
  const isAdmin = userRoles.includes("ADMIN") || userRoles.includes("ADMIN_ENTERPRISE");

  // Verificar si el producto está marcado como revisado cuando se abre el modal
  useEffect(() => {
    if (product && open) {
      const reviewed = isProductReviewed(product.id);
      setIsReviewed(reviewed);
    }
  }, [product, open]);

  if (!open || !product) return null;
  const statusUpper = String(product.status).toUpperCase();
  const statusLower = String(product.status).toLowerCase();
  const showActions = showApproveReject || statusUpper === 'FAILED';
  const showCancelButton = statusUpper === 'PENDING' && typeof onCancelOrder === 'function';
  const statusStyle = STATUS_COLORS[statusLower] || STATUS_COLORS.pending;
  const isTerminalStatus = statusUpper === 'APPROVED' || statusUpper === 'REJECTED' || statusUpper === 'CANCELLED';

  // Verificar si hay dimensiones del producto
  const hasProductDimensions = product.length || product.width || product.height;
  const hasPackageDimensions = product.packageLength || product.packageWidth || product.packageHeight;
  const hasDimensions = hasProductDimensions || hasPackageDimensions;

  // Verificar si hay especificaciones
  const hasSpecs = product.composition || product.unitOfMeasureName || product.originOfGoodsName || 
                   product.invoicePolicy || product.hasExpirationDate !== undefined;

  // Función para manejar el marcado como revisado
  const handleMarkAsReviewed = async () => {
    if (reviewLoading) return;

    setReviewLoading(true);
    try {
      if (onMarkAsReviewed) {
        await onMarkAsReviewed(product);
        setIsReviewed(true);
        
        if (product && product.id) {
          window.dispatchEvent(new CustomEvent('productReviewed', {
            detail: { productId: product.id }
          }));
        }
        
        onClose();
      }
    } catch (error) {
      console.error('Error al marcar como revisado:', error);
    } finally {
      setReviewLoading(false);
    }
  };

  return (
    <>
      <style>{styles}</style>
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        background: 'rgba(0,0,0,0.35)',
        zIndex: 1200,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        <div style={{
          position: 'relative',
          width: '100%',
          maxWidth: '900px',
          maxHeight: '92vh',
          background: '#fff',
          borderRadius: 16,
          boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          animation: 'modalFadeIn 0.25s ease-out',
        }}>
          {/* Header fijo */}
          <div style={{
            position: 'sticky',
            top: 0,
            background: '#fff',
            zIndex: 10,
            padding: '20px 32px 16px',
            borderBottom: '1px solid #eef1f5',
            borderRadius: '16px 16px 0 0',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div style={{ flex: 1 }}>
                <h2 style={{ fontWeight: 700, fontSize: 22, margin: 0, color: '#1a1a2e', lineHeight: 1.3 }}>
                  {product.productName}
                </h2>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 8, flexWrap: 'wrap' }}>
                  {/* Badge de estado */}
                  <span style={{
                    background: statusStyle.bg,
                    color: statusStyle.color,
                    border: `1px solid ${statusStyle.border}`,
                    fontWeight: 700,
                    fontSize: 12,
                    borderRadius: 20,
                    padding: '4px 14px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 5,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    <FontAwesomeIcon icon={statusUpper === 'APPROVED' ? faCheckCircle : statusUpper === 'REJECTED' ? faTimesCircle : faCalendarAlt} style={{ fontSize: 11 }} />
                    {STATUS_LABELS[statusLower] || product.status}
                  </span>
                  {/* Badge simple/variantes */}
                  <span style={{
                    background: '#f0f2ff',
                    color: '#5b6abf',
                    fontWeight: 600,
                    fontSize: 12,
                    borderRadius: 20,
                    padding: '4px 14px',
                    border: '1px solid #dde1ff',
                  }}>
                    {product.isSimple ? 'Producto simple' : `${product.variantsCount || 0} variante${(product.variantsCount || 0) !== 1 ? 's' : ''}`}
                  </span>
                  {/* Indicador de revisado - Solo visible para administradores */}
                  {isAdmin && (statusUpper === 'PENDING' || statusUpper === 'FAILED') && (
                    <span style={{
                      background: isReviewed ? '#e8f5e9' : '#fff8e1',
                      color: isReviewed ? '#2e7d32' : '#f57f17',
                      border: `1px solid ${isReviewed ? '#a5d6a7' : '#ffe082'}`,
                      fontWeight: 700,
                      fontSize: 12,
                      borderRadius: 20,
                      padding: '4px 14px',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 5,
                    }}>
                      <FontAwesomeIcon icon={isReviewed ? faEye : faEyeSlash} style={{ fontSize: 11 }} />
                      {isReviewed ? 'Revisado' : 'No revisado'}
                    </span>
                  )}
                </div>
              </div>
              <button onClick={onClose} style={{
                background: '#f5f5f5',
                border: 'none',
                outline: 'none',
                fontSize: 22,
                color: '#666',
                cursor: 'pointer',
                borderRadius: '50%',
                width: 36,
                height: 36,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s',
                flexShrink: 0,
              }}
              onMouseEnter={e => { e.currentTarget.style.background = '#e0e0e0'; }}
              onMouseLeave={e => { e.currentTarget.style.background = '#f5f5f5'; }}
              title="Cerrar">×</button>
            </div>
          </div>

          {/* Contenido con scroll */}
          <div style={{ padding: '20px 32px 24px', flex: 1 }}>
            {/* Motivo de rechazo */}
            {statusUpper === 'REJECTED' && product.reviewerComments && (
              <div style={{
                background: '#fff5f5',
                border: '1px solid #fed7d7',
                borderRadius: 10,
                padding: '12px 16px',
                marginBottom: 20,
                display: 'flex',
                alignItems: 'flex-start',
                gap: 10,
              }}>
                <FontAwesomeIcon icon={faTimesCircle} style={{ color: '#c62828', marginTop: 2, flexShrink: 0 }} />
                <div>
                  <div style={{ fontWeight: 700, fontSize: 13, color: '#c62828', marginBottom: 4 }}>Motivo del rechazo</div>
                  <div style={{ fontSize: 14, color: '#5f2120', whiteSpace: 'pre-line' }}>{product.reviewerComments}</div>
                </div>
              </div>
            )}

            {/* Imagen principal - solo si no es estado terminal */}
            {!isTerminalStatus && product.mainImage && (
              <div
                onClick={() => setImageModalOpen(true)}
                style={{
                  cursor: 'pointer',
                  position: 'relative',
                  marginBottom: 20,
                  borderRadius: 12,
                  overflow: 'hidden',
                  border: '1px solid #eee',
                }}
              >
                <img
                  src={convertBase64ToDataUrl(product.mainImage)}
                  alt={product.productName}
                  style={{
                    width: '100%',
                    maxHeight: 220,
                    objectFit: 'cover',
                    display: 'block',
                    transition: 'transform 0.3s ease',
                  }}
                  onMouseEnter={(e) => { e.target.style.transform = 'scale(1.03)'; }}
                  onMouseLeave={(e) => { e.target.style.transform = 'scale(1)'; }}
                />
                <div style={{
                  position: 'absolute',
                  bottom: 10,
                  right: 10,
                  background: 'rgba(0,0,0,0.65)',
                  color: 'white',
                  padding: '5px 12px',
                  borderRadius: 16,
                  fontSize: 12,
                  fontWeight: 600,
                }}>
                  Clic para ampliar
                </div>
              </div>
            )}

            {/* Sección: Información General */}
            <Section icon={faInfoCircle} title="Información General">
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 16px' }}>
                <DetailField label="Proveedor" value={product.ownerName} />
                <DetailField label="Marca" value={product.brand} />
                <DetailField label="Categoría" value={product.categoryName ? `${product.categoryName} (ID: ${product.categoryId})` : null} />
                <DetailField label="Empresa" value={product.enterprise?.name} />
                <DetailField label="Fecha de envío" value={product.submittedAt ? new Date(product.submittedAt).toLocaleString('es-ES', { dateStyle: 'medium', timeStyle: 'short' }) : null} />
                <DetailField label="Creado por" value={product.createdBy?.name ? `${product.createdBy.name} (${product.createdBy.email})` : null} />
              </div>
              {product.description && (
                <div style={{ marginTop: 10 }}>
                  <div style={{ fontSize: 12, color: '#8899a6', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>
                    Descripción
                  </div>
                  <div style={{
                    fontSize: 14,
                    color: '#444',
                    background: '#f8f9fb',
                    borderRadius: 8,
                    padding: '10px 14px',
                    whiteSpace: 'pre-line',
                    lineHeight: 1.5,
                    border: '1px solid #eef1f5',
                  }}>
                    {product.description}
                  </div>
                </div>
              )}
            </Section>

            {/* Sección: Categorías Web */}
            {product.webCategories && product.webCategories.length > 0 && (
              <Section icon={faGlobe} title="Categorías Web">
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {product.webCategories.map((cat, idx) => (
                    <span key={idx} style={{
                      background: '#f0f2ff',
                      color: '#4a55a2',
                      fontSize: 13,
                      fontWeight: 600,
                      borderRadius: 20,
                      padding: '5px 14px',
                      border: '1px solid #dde1ff',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 5,
                    }}>
                      <FontAwesomeIcon icon={faTag} style={{ fontSize: 10 }} />
                      {cat.categoryName}
                    </span>
                  ))}
                </div>
              </Section>
            )}

            {/* Sección: Especificaciones */}
            {hasSpecs && (
              <Section icon={faFileInvoice} title="Especificaciones">
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 16px' }}>
                  <DetailField label="Composición" value={product.composition} fullWidth />
                  <DetailField label="Unidad de medida" value={product.unitOfMeasureName} />
                  <DetailField label="Origen de la mercancía" value={product.originOfGoodsName} />
                  <DetailField label="Política de facturación" value={product.invoicePolicy ? INVOICE_POLICY_LABELS[product.invoicePolicy] || product.invoicePolicy : null} />
                  <DetailField label="¿Tiene fecha de vencimiento?" value={product.hasExpirationDate !== undefined ? product.hasExpirationDate : null} />
                </div>
              </Section>
            )}

            {/* Sección: Dimensiones */}
            {hasDimensions && (
              <Section icon={faRulerCombined} title="Dimensiones">
                <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                  {hasProductDimensions && (
                    <div style={{
                      flex: '1 1 calc(50% - 8px)',
                      minWidth: 200,
                      background: '#f8f9fb',
                      borderRadius: 10,
                      padding: '12px 16px',
                      border: '1px solid #eef1f5',
                    }}>
                      <div style={{ fontWeight: 700, fontSize: 13, color: '#5b6abf', marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        📦 Producto
                      </div>
                      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                        {product.length !== undefined && product.length !== null && (
                          <div style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: 11, color: '#8899a6', fontWeight: 600 }}>Largo</div>
                            <div style={{ fontSize: 16, fontWeight: 700, color: '#2c3e50' }}>{product.length}<span style={{ fontSize: 11, color: '#8899a6' }}> cm</span></div>
                          </div>
                        )}
                        {product.width !== undefined && product.width !== null && (
                          <div style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: 11, color: '#8899a6', fontWeight: 600 }}>Ancho</div>
                            <div style={{ fontSize: 16, fontWeight: 700, color: '#2c3e50' }}>{product.width}<span style={{ fontSize: 11, color: '#8899a6' }}> cm</span></div>
                          </div>
                        )}
                        {product.height !== undefined && product.height !== null && (
                          <div style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: 11, color: '#8899a6', fontWeight: 600 }}>Alto</div>
                            <div style={{ fontSize: 16, fontWeight: 700, color: '#2c3e50' }}>{product.height}<span style={{ fontSize: 11, color: '#8899a6' }}> cm</span></div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  {hasPackageDimensions && (
                    <div style={{
                      flex: '1 1 calc(50% - 8px)',
                      minWidth: 200,
                      background: '#f8f9fb',
                      borderRadius: 10,
                      padding: '12px 16px',
                      border: '1px solid #eef1f5',
                    }}>
                      <div style={{ fontWeight: 700, fontSize: 13, color: '#5b6abf', marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        📤 Paquete
                      </div>
                      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                        {product.packageLength !== undefined && product.packageLength !== null && (
                          <div style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: 11, color: '#8899a6', fontWeight: 600 }}>Largo</div>
                            <div style={{ fontSize: 16, fontWeight: 700, color: '#2c3e50' }}>{product.packageLength}<span style={{ fontSize: 11, color: '#8899a6' }}> cm</span></div>
                          </div>
                        )}
                        {product.packageWidth !== undefined && product.packageWidth !== null && (
                          <div style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: 11, color: '#8899a6', fontWeight: 600 }}>Ancho</div>
                            <div style={{ fontSize: 16, fontWeight: 700, color: '#2c3e50' }}>{product.packageWidth}<span style={{ fontSize: 11, color: '#8899a6' }}> cm</span></div>
                          </div>
                        )}
                        {product.packageHeight !== undefined && product.packageHeight !== null && (
                          <div style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: 11, color: '#8899a6', fontWeight: 600 }}>Alto</div>
                            <div style={{ fontSize: 16, fontWeight: 700, color: '#2c3e50' }}>{product.packageHeight}<span style={{ fontSize: 11, color: '#8899a6' }}> cm</span></div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </Section>
            )}

            {/* Sección: Atributos */}
            <Section icon={faCubes} title="Atributos">
              {product.attributes && product.attributes.length > 0 ? (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {product.attributes.map(attr => (
                    <div key={attr.attributeId} style={{
                      background: '#f8f9fb',
                      borderRadius: 8,
                      padding: '8px 14px',
                      border: '1px solid #eef1f5',
                      flex: '1 1 calc(50% - 4px)',
                      minWidth: 200,
                    }}>
                      <div style={{ fontSize: 12, color: '#8899a6', fontWeight: 600, marginBottom: 4 }}>{attr.attributeName}</div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                        {attr.valueNames.map((val, idx) => (
                          <span key={idx} style={{
                            background: '#e8ecff',
                            color: '#4a55a2',
                            fontSize: 12,
                            fontWeight: 600,
                            borderRadius: 12,
                            padding: '2px 10px',
                          }}>
                            {val}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ color: '#aaa', fontSize: 14, fontStyle: 'italic' }}>Sin atributos definidos</div>
              )}
            </Section>

            {/* Sección: Variantes */}
            <Section icon={faBoxOpen} title="Variantes">
              {product.variantAttributeMapping && product.variantAttributeMapping.length > 0 ? (
                <div style={{ overflowX: 'auto', borderRadius: 10, border: '1px solid #eef1f5' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead>
                      <tr style={{ background: '#f8f9fb' }}>
                        {/* Imagen solo en estados no terminales */}
                        {!isTerminalStatus && (
                          <th style={{ padding: '10px 8px', borderBottom: '2px solid #e8ecf1', textAlign: 'center', fontWeight: 700, color: '#5b6abf', fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.5px', whiteSpace: 'nowrap' }}>Imagen</th>
                        )}
                        <th style={{ padding: '10px 8px', borderBottom: '2px solid #e8ecf1', textAlign: 'left', fontWeight: 700, color: '#5b6abf', fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.5px', whiteSpace: 'nowrap' }}>Referencia</th>
                        <th style={{ padding: '10px 8px', borderBottom: '2px solid #e8ecf1', textAlign: 'left', fontWeight: 700, color: '#5b6abf', fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.5px', whiteSpace: 'nowrap' }}>Código barras</th>
                        <th style={{ padding: '10px 8px', borderBottom: '2px solid #e8ecf1', textAlign: 'center', fontWeight: 700, color: '#5b6abf', fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.5px', whiteSpace: 'nowrap' }}>Peso (kg)</th>
                        <th style={{ padding: '10px 8px', borderBottom: '2px solid #e8ecf1', textAlign: 'center', fontWeight: 700, color: '#5b6abf', fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.5px', whiteSpace: 'nowrap' }}>Volumen (m³)</th>
                        <th style={{ padding: '10px 8px', borderBottom: '2px solid #e8ecf1', textAlign: 'left', fontWeight: 700, color: '#5b6abf', fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Atributos</th>
                        {/* Acciones solo en estados no terminales */}
                        {!isTerminalStatus && (
                          <th style={{ padding: '10px 8px', borderBottom: '2px solid #e8ecf1', textAlign: 'center', fontWeight: 700, color: '#5b6abf', fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Acciones</th>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {product.variantAttributeMapping.map((variant, idx) => (
                        <tr key={idx} style={{
                          background: idx % 2 === 0 ? '#fff' : '#fafbfd',
                          transition: 'background 0.15s',
                        }}
                        onMouseEnter={e => { e.currentTarget.style.background = '#f0f4ff'; }}
                        onMouseLeave={e => { e.currentTarget.style.background = idx % 2 === 0 ? '#fff' : '#fafbfd'; }}
                        >
                          {/* Imagen */}
                          {!isTerminalStatus && (
                            <td style={{ padding: '8px', borderBottom: '1px solid #eef1f5', textAlign: 'center' }}>
                              {variant.image ? (
                                <img src={convertBase64ToDataUrl(variant.image)} alt={variant.internalReference} style={{ width: 42, height: 42, objectFit: 'cover', borderRadius: 6, border: '1px solid #eee' }} />
                              ) : (
                                <div style={{ width: 42, height: 42, borderRadius: 6, background: '#f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto', color: '#ccc', fontSize: 18 }}>?</div>
                              )}
                            </td>
                          )}
                          <td style={{ padding: '8px', borderBottom: '1px solid #eef1f5', fontWeight: 500, color: '#2c3e50' }}>{variant.internalReference}</td>
                          <td style={{ padding: '8px', borderBottom: '1px solid #eef1f5', color: '#555', fontFamily: 'monospace', fontSize: 12 }}>{variant.barcode}</td>
                          <td style={{ padding: '8px', borderBottom: '1px solid #eef1f5', textAlign: 'center', color: '#555' }}>{variant.weight || '-'}</td>
                          <td style={{ padding: '8px', borderBottom: '1px solid #eef1f5', textAlign: 'center', color: '#555' }}>{variant.volume || '-'}</td>
                          <td style={{ padding: '8px', borderBottom: '1px solid #eef1f5', color: '#555', fontSize: 12 }}>
                            {variant.attributeValues && variant.attributeValues.length > 0
                              ? variant.attributeValues.map(av => `${av.attributeName}: ${av.valueName}`).join(' · ')
                              : <span style={{ color: '#bbb', fontStyle: 'italic' }}>Sin atributos</span>}
                          </td>
                          {/* Acciones */}
                          {!isTerminalStatus && (
                            <td style={{ padding: '8px', borderBottom: '1px solid #eef1f5', textAlign: 'center' }}>
                              {Array.isArray(variant.additionalImages) && variant.additionalImages.length > 0 ? (
                                <button
                                  style={{
                                    background: 'linear-gradient(135deg, #5b6abf 0%, #7c8adb 100%)',
                                    color: '#fff',
                                    border: 'none',
                                    borderRadius: 6,
                                    padding: '5px 10px',
                                    fontWeight: 600,
                                    cursor: 'pointer',
                                    fontSize: 11,
                                    whiteSpace: 'nowrap',
                                    transition: 'opacity 0.2s',
                                  }}
                                  onClick={() => onShowAdditionalImages && onShowAdditionalImages(variant.additionalImages)}
                                  onMouseEnter={e => { e.target.style.opacity = '0.85'; }}
                                  onMouseLeave={e => { e.target.style.opacity = '1'; }}
                                >
                                  📷 Ver imágenes
                                </button>
                              ) : (
                                <span style={{ color: '#bbb', fontSize: 11 }}>—</span>
                              )}
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div style={{ color: '#aaa', fontSize: 14, fontStyle: 'italic' }}>Sin variantes definidas</div>
              )}
            </Section>

            {/* Imágenes adicionales del template */}
            {!isTerminalStatus && product.additionalImages && product.additionalImages.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <button
                  style={{
                    background: 'linear-gradient(135deg, #5b6abf 0%, #7c8adb 100%)',
                    color: '#fff',
                    border: 'none',
                    borderRadius: 10,
                    padding: '10px 20px',
                    fontWeight: 600,
                    cursor: 'pointer',
                    fontSize: 14,
                    width: '100%',
                    transition: 'opacity 0.2s',
                  }}
                  onClick={() => onShowAdditionalImages && onShowAdditionalImages(product.additionalImages)}
                  onMouseEnter={e => { e.target.style.opacity = '0.9'; }}
                  onMouseLeave={e => { e.target.style.opacity = '1'; }}
                >
                  📷 Ver {product.additionalImages.length} imágen{product.additionalImages.length !== 1 ? 'es' : ''} adicional{product.additionalImages.length !== 1 ? 'es' : ''} del producto
                </button>
              </div>
            )}

            {/* Botones de aprobar/rechazar */}
            {showActions && (
              <div style={{ display: 'flex', gap: 12, marginTop: 8 }}>
                <button
                  style={{
                    flex: 1,
                    background: '#fff',
                    border: '2px solid #ef5350',
                    color: '#ef5350',
                    borderRadius: 10,
                    padding: '12px 0',
                    fontWeight: 700,
                    cursor: 'pointer',
                    fontSize: 15,
                    transition: 'all 0.2s',
                  }}
                  onClick={onReject}
                  disabled={rejectDisabled}
                  onMouseEnter={e => { e.currentTarget.style.background = '#ffebee'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = '#fff'; }}
                >
                  <FontAwesomeIcon icon={faTimesCircle} style={{ marginRight: 6 }} /> Rechazar
                </button>
                <button
                  style={{
                    flex: 1,
                    background: '#27ae60',
                    border: '2px solid #27ae60',
                    color: '#fff',
                    borderRadius: 10,
                    padding: '12px 0',
                    fontWeight: 700,
                    cursor: 'pointer',
                    fontSize: 15,
                    transition: 'all 0.2s',
                  }}
                  onClick={onApprove}
                  disabled={approveDisabled}
                  onMouseEnter={e => { e.currentTarget.style.background = '#219a52'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = '#27ae60'; }}
                >
                  <FontAwesomeIcon icon={faCheckCircle} style={{ marginRight: 6 }} /> Aprobar
                </button>
              </div>
            )}

            {/* Botón para marcar como revisado */}
            {showReviewButton && isAdmin && (statusUpper === 'PENDING' || statusUpper === 'FAILED') && !isReviewed && (
              <button
                style={{
                  width: '100%',
                  background: 'linear-gradient(135deg, #5b6abf 0%, #7c8adb 100%)',
                  border: 'none',
                  color: '#fff',
                  borderRadius: 12,
                  padding: '16px 0',
                  fontWeight: 700,
                  cursor: reviewLoading ? 'not-allowed' : 'pointer',
                  fontSize: 14,
                  opacity: reviewLoading ? 0.7 : 1,
                  marginTop: 16,
                  boxShadow: '0 6px 20px rgba(91, 106, 191, 0.3)',
                  transition: 'all 0.3s ease',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 10,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}
                onClick={handleMarkAsReviewed}
                disabled={reviewLoading}
                onMouseEnter={(e) => {
                  if (!reviewLoading) {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 10px 30px rgba(91, 106, 191, 0.4)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!reviewLoading) {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 6px 20px rgba(91, 106, 191, 0.3)';
                  }
                }}
              >
                {reviewLoading ? (
                  <>
                    <div style={{
                      width: 18,
                      height: 18,
                      border: '3px solid #ffffff40',
                      borderTop: '3px solid #fff',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }}></div>
                    Marcando como revisado...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faEye} style={{ fontSize: 14 }} />
                    Marcar como revisado
                  </>
                )}
              </button>
            )}

            {/* Botón cancelar orden */}
            {showCancelButton && (
              <button
                style={{
                  width: '100%',
                  marginTop: 12,
                  background: '#fff',
                  border: '2px solid #ef5350',
                  color: '#ef5350',
                  borderRadius: 10,
                  padding: '12px 0',
                  fontWeight: 700,
                  cursor: 'pointer',
                  fontSize: 15,
                  transition: 'all 0.2s',
                }}
                onClick={() => onCancelOrder(product)}
                onMouseEnter={e => { e.currentTarget.style.background = '#ffebee'; }}
                onMouseLeave={e => { e.currentTarget.style.background = '#fff'; }}
              >
                Cancelar orden
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Modal para imagen ampliada */}
      {imageModalOpen && product.mainImage && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          background: 'rgba(0,0,0,0.92)',
          zIndex: 1300,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 20
        }}>
          <button
            onClick={() => setImageModalOpen(false)}
            style={{
              position: 'absolute',
              top: 20,
              right: 20,
              background: 'rgba(255,255,255,0.15)',
              border: 'none',
              fontSize: 28,
              color: 'white',
              cursor: 'pointer',
              borderRadius: '50%',
              width: 46,
              height: 46,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'background 0.2s ease'
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.25)'}
            onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.15)'}
            title="Cerrar"
          >
            ×
          </button>
          <img
            src={convertBase64ToDataUrl(product.mainImage)}
            alt={product.productName}
            style={{
              maxWidth: '90vw',
              maxHeight: '90vh',
              objectFit: 'contain',
              borderRadius: 8
            }}
          />
        </div>
      )}
    </>
  );
};

export default ProductModalWidget;